import {
  RULES_VERSION,
  checkEpitaphReplay,
  legalLinkOptions,
  onBoardPawns,
  opponent,
  type Action,
  type CrownMode,
  type MatchConfig,
  type MatchEvent,
  type MatchMode,
  type MatchResult,
  type MatchState,
  type PassKind,
  type PresetId,
  type Side,
} from '../engine';
import { bestChainScore } from './search';

/**
 * Playtest telemetry (spec §21). A recorder consumes every applied action with the states before
 * and after it; the records support the success criteria of §25.2.
 */

export interface TurnRecord {
  readonly round: number;
  readonly turn: number;
  readonly side: Side;
  readonly action: 'chain' | 'epitaph' | 'recover' | 'pass';
  readonly passKind: PassKind | null;
  readonly cardsSpent: number;
  readonly score: number;
  readonly hand: Readonly<Record<Side, number>>;
  readonly deck: Readonly<Record<Side, number>>;
}

export interface CaptureChance {
  readonly round: number;
  readonly turn: number;
  readonly side: Side;
  /** Whether any legal first link could capture an inscribed enemy pawn. */
  readonly couldCaptureInscribed: boolean;
  /** Whether the chosen action captured an inscribed enemy pawn. */
  capturedInscribed: boolean;
  /** Best immediate chain score available (null when not computed). */
  readonly bestChainScore: number | null;
  /** Score of the chosen action. */
  chosenScore: number;
  chosenAction: 'chain' | 'epitaph' | 'recover';
}

export interface PassRecord {
  readonly round: number;
  readonly turn: number;
  readonly side: Side;
  readonly kind: PassKind;
  /** Passer's round score minus the opponent's. */
  readonly scoreDiff: number;
  /** Cards left (hand + deck) per side. */
  readonly cardsLeft: Readonly<Record<Side, number>>;
  /** True when the opponent had not passed yet. */
  readonly first: boolean;
}

export interface EpitaphChance {
  readonly round: number;
  readonly turn: number;
  readonly side: Side;
  readonly played: boolean;
  readonly liveScore: number;
  readonly bestChainScore: number | null;
}

export interface RoundRecord {
  readonly round: number;
  readonly starter: Side;
  readonly winner: Side | null;
  readonly scores: Readonly<Record<Side, number>>;
  readonly firstCaptureTurn: number | null;
  readonly firstCrownTurn: number | null;
  /** Captures answered by a capture on the victim side's next turn. */
  readonly recaptures: number;
  readonly captures: number;
}

export interface MatchTelemetry {
  readonly context: {
    readonly rulesVersion: string;
    /** Version of the game build that recorded the match, when known. */
    readonly appVersion?: string;
    readonly preset: PresetId;
    readonly mode: MatchMode;
    readonly seed: string | number;
    readonly homeRowImmunity: boolean;
    readonly crownMode: CrownMode;
    readonly players: Readonly<Record<Side, string>>;
  };
  readonly turns: TurnRecord[];
  readonly captureChances: CaptureChance[];
  readonly passes: PassRecord[];
  readonly epitaphChances: EpitaphChance[];
  readonly rounds: RoundRecord[];
  readonly result: MatchResult | null;
}

export interface TelemetryOptions {
  /** Anonymous player ids per side (§21.1). */
  readonly anonymousIds?: Readonly<Record<Side, string>>;
  /** Compute the best available chain score at each ACTION phase (§21.4); costs search time. */
  readonly bestScores?: boolean;
  /** Version of the game build, recorded in the match context. */
  readonly appVersion?: string;
}

interface OpenTurn {
  round: number;
  turn: number;
  side: Side;
  action: TurnRecord['action'] | null;
  passKind: PassKind | null;
  cards: number;
  score: number;
}

export class TelemetryRecorder {
  private context: MatchTelemetry['context'] | null = null;
  private readonly turns: TurnRecord[] = [];
  private readonly captureChances: CaptureChance[] = [];
  private readonly passes: PassRecord[] = [];
  private readonly epitaphChances: EpitaphChance[] = [];
  private readonly rounds: RoundRecord[] = [];
  private open: OpenTurn | null = null;
  private pendingChance: CaptureChance | null = null;
  private roundFirstCapture: number | null = null;
  private roundFirstCrown: number | null = null;
  private roundCaptures = 0;
  private roundRecaptures = 0;
  private lastCaptureBy: { side: Side; turn: number } | null = null;

  constructor(private readonly options: TelemetryOptions = {}) {}

  /** Records the match context (§21.1). Call once with the config, seed and setup events. */
  start(
    config: MatchConfig,
    seed: string | number,
    _setupEvents: readonly MatchEvent[] = [],
  ): void {
    this.context = {
      rulesVersion: RULES_VERSION,
      ...(this.options.appVersion ? { appVersion: this.options.appVersion } : {}),
      preset: config.preset,
      mode: config.mode,
      seed,
      homeRowImmunity: config.homeRowImmunity,
      crownMode: config.crownMode,
      players: this.options.anonymousIds ?? { S: 'south', N: 'north' },
    };
  }

  /** Records one applied action. */
  record(
    before: MatchState,
    action: Action,
    after: MatchState,
    events: readonly MatchEvent[],
  ): void {
    if (before.phase === 'ACTION' && action.side === before.decider)
      this.onActionPhase(before, action);
    for (const e of events) this.onEvent(e, before, after);
  }

  private onActionPhase(s: MatchState, action: Action): void {
    const side = s.decider;
    const enemy = opponent(side);
    const options = legalLinkOptions(s, side);
    const couldCaptureInscribed = options.some((lo) =>
      lo.option.captures.some((id) => s.pawns[id]?.side === enemy && !!s.pawns[id]?.inscription),
    );
    const best = this.options.bestScores === false ? null : bestChainScore(s, side).score;
    this.pendingChance = {
      round: s.round,
      turn: s.turn,
      side,
      couldCaptureInscribed,
      capturedInscribed: false,
      bestChainScore: best,
      chosenScore: 0,
      chosenAction:
        action.type === 'PLAY_EPITAPH'
          ? 'epitaph'
          : action.type === 'RECOVER'
            ? 'recover'
            : 'chain',
    };
    this.captureChances.push(this.pendingChance);
    const pl = s.players[side];
    if (pl.epitaphs.length > 0 && pl.epitaphPlays < s.config.constants.EPITAPH_PLAYS_PER_ROUND) {
      let live = -1;
      for (const e of pl.epitaphs)
        for (const p of onBoardPawns(s.pawns, side)) {
          const r = checkEpitaphReplay(s, side, e, p.id);
          if (r.ok) live = Math.max(live, r.breakdown.score);
        }
      if (live >= 0)
        this.epitaphChances.push({
          round: s.round,
          turn: s.turn,
          side,
          played: action.type === 'PLAY_EPITAPH',
          liveScore: live,
          bestChainScore: best,
        });
    }
  }

  private flushTurn(after: MatchState): void {
    const t = this.open;
    if (!t) return;
    this.open = null;
    this.turns.push({
      round: t.round,
      turn: t.turn,
      side: t.side,
      action: t.action ?? 'pass',
      passKind: t.passKind,
      cardsSpent: t.cards,
      score: t.score,
      hand: { S: after.players.S.hand.length, N: after.players.N.hand.length },
      deck: { S: after.players.S.deck.length, N: after.players.N.deck.length },
    });
  }

  private onEvent(e: MatchEvent, before: MatchState, after: MatchState): void {
    switch (e.type) {
      case 'TurnStarted':
        this.flushTurn(after);
        this.pendingChance = null;
        this.open = {
          round: e.round,
          turn: e.turn,
          side: e.side,
          action: null,
          passKind: null,
          cards: 0,
          score: 0,
        };
        break;
      case 'Passed': {
        if (this.open && this.open.side === e.side) {
          this.open.action = 'pass';
          this.open.passKind = e.kind;
        }
        const other = opponent(e.side);
        // Scores and cards as they stood when the pass happened (before the round could end).
        const src = before;
        this.passes.push({
          round: e.round,
          turn: src.turn,
          side: e.side,
          kind: e.kind,
          scoreDiff: src.players[e.side].roundScore - src.players[other].roundScore,
          cardsLeft: {
            S: src.players.S.hand.length + src.players.S.deck.length,
            N: src.players.N.hand.length + src.players.N.deck.length,
          },
          first: !src.players[other].passed,
        });
        break;
      }
      case 'LinkPlayed':
        if (this.open) {
          this.open.action = 'chain';
          this.open.cards += 1;
        }
        break;
      case 'Recovered':
        if (this.open) {
          this.open.action = 'recover';
          this.open.cards += 1;
        }
        break;
      case 'EpitaphPlayed':
        if (this.open) this.open.action = 'epitaph';
        break;
      case 'ChainScored':
        if (this.open && this.open.side === e.side) this.open.score += e.breakdown.score;
        if (this.pendingChance && this.pendingChance.side === e.side)
          this.pendingChance.chosenScore = e.breakdown.score;
        break;
      case 'PawnCaptured': {
        const turn = before.turn;
        if (this.roundFirstCapture === null) this.roundFirstCapture = turn;
        this.roundCaptures++;
        const capturer = opponent(e.side);
        if (
          this.lastCaptureBy &&
          this.lastCaptureBy.side === e.side &&
          turn === this.lastCaptureBy.turn + 1
        )
          this.roundRecaptures++;
        this.lastCaptureBy = { side: capturer, turn };
        if (e.epitaphId && this.pendingChance && this.pendingChance.side === capturer)
          this.pendingChance.capturedInscribed = true;
        break;
      }
      case 'PawnCrowned':
        if (this.roundFirstCrown === null) this.roundFirstCrown = before.turn;
        break;
      case 'RoundEnded':
        this.flushTurn(after);
        this.rounds.push({
          round: e.round,
          starter: before.roundStarter,
          winner: e.winner,
          scores: e.scores,
          firstCaptureTurn: this.roundFirstCapture,
          firstCrownTurn: this.roundFirstCrown,
          recaptures: this.roundRecaptures,
          captures: this.roundCaptures,
        });
        this.roundFirstCapture = null;
        this.roundFirstCrown = null;
        this.roundCaptures = 0;
        this.roundRecaptures = 0;
        this.lastCaptureBy = null;
        break;
      default:
        break;
    }
  }

  finish(final: MatchState): MatchTelemetry {
    this.flushTurn(final);
    return {
      context: this.context ?? {
        rulesVersion: final.rulesVersion,
        ...(this.options.appVersion ? { appVersion: this.options.appVersion } : {}),
        preset: final.config.preset,
        mode: final.config.mode,
        seed: '',
        homeRowImmunity: final.config.homeRowImmunity,
        crownMode: final.config.crownMode,
        players: this.options.anonymousIds ?? { S: 'south', N: 'north' },
      },
      turns: this.turns,
      captureChances: this.captureChances,
      passes: this.passes,
      epitaphChances: this.epitaphChances,
      rounds: this.rounds,
      result: final.result,
    };
  }
}

export interface TelemetrySummary {
  readonly matches: number;
  readonly captureRefusalRate: number | null;
  readonly nonGreedyChainRate: number | null;
  readonly earlyPassWinRate: number | null;
  readonly earlyPasses: number;
  readonly epitaphPlayRate: number | null;
  readonly epitaphBeatsChainRate: number | null;
  readonly starterRoundWinRate: number | null;
  readonly attackOpeningRate: number | null;
  readonly recaptureRate: number | null;
  readonly lines: string[];
}

const ratio = (n: number, d: number): number | null => (d === 0 ? null : n / d);
const fmt = (r: number | null): string => (r === null ? 'n/a' : `${(100 * r).toFixed(1)}%`);

/** Aggregates telemetry into the §25.2 playtest criteria. */
export function summarizeTelemetry(all: readonly MatchTelemetry[]): TelemetrySummary {
  let refusalChances = 0;
  let refusals = 0;
  let chainChances = 0;
  let nonGreedy = 0;
  let earlyPasses = 0;
  let earlyPassWins = 0;
  let epitaphChances = 0;
  let epitaphPlays = 0;
  let epitaphBetter = 0;
  let rounds = 0;
  let starterWins = 0;
  let decided = 0;
  let attackOpenings = 0;
  let captures = 0;
  let recaptures = 0;
  for (const m of all) {
    for (const c of m.captureChances) {
      if (c.couldCaptureInscribed) {
        refusalChances++;
        if (!c.capturedInscribed) refusals++;
      }
      if (c.bestChainScore !== null && c.bestChainScore > 0) {
        chainChances++;
        if (c.chosenScore < c.bestChainScore) nonGreedy++;
      }
    }
    for (const p of m.passes) {
      if (p.kind !== 'voluntary' || !p.first) continue;
      earlyPasses++;
      if (m.result?.winner === p.side) earlyPassWins++;
    }
    for (const e of m.epitaphChances) {
      epitaphChances++;
      if (e.played) epitaphPlays++;
      if (e.bestChainScore !== null && e.liveScore >= e.bestChainScore) epitaphBetter++;
    }
    for (const r of m.rounds) {
      rounds++;
      if (r.winner !== null) {
        decided++;
        if (r.winner === r.starter) starterWins++;
      }
      if (r.firstCaptureTurn !== null && r.firstCaptureTurn <= 4) attackOpenings++;
      captures += r.captures;
      recaptures += r.recaptures;
    }
  }
  const summary = {
    matches: all.length,
    captureRefusalRate: ratio(refusals, refusalChances),
    nonGreedyChainRate: ratio(nonGreedy, chainChances),
    earlyPassWinRate: ratio(earlyPassWins, earlyPasses),
    earlyPasses,
    epitaphPlayRate: ratio(epitaphPlays, epitaphChances),
    epitaphBeatsChainRate: ratio(epitaphBetter, epitaphChances),
    starterRoundWinRate: ratio(starterWins, decided),
    attackOpeningRate: ratio(attackOpenings, rounds),
    recaptureRate: ratio(recaptures, captures),
  };
  return {
    ...summary,
    lines: [
      `1. Inscribed captures turned down: ${fmt(summary.captureRefusalRate)} of ${refusalChances} chances`,
      `2. Chains chosen below the best immediate score: ${fmt(summary.nonGreedyChainRate)} of ${chainChances} actions`,
      `3. First voluntary passes by the eventual match winner: ${fmt(summary.earlyPassWinRate)} of ${earlyPasses}`,
      `4. Playable Epitaphs replayed: ${fmt(summary.epitaphPlayRate)} of ${epitaphChances}; replay ≥ best chain: ${fmt(summary.epitaphBeatsChainRate)}`,
      `5. Round starter win rate: ${fmt(summary.starterRoundWinRate)} of ${decided} decided rounds`,
      `   Rounds opening with a capture by turn 4: ${fmt(summary.attackOpeningRate)}; recaptures: ${fmt(summary.recaptureRate)} of ${captures} captures`,
    ],
  };
}
